import { auth } from '@/constants/firebaseConfig'
import { getFirebaseAuthToken } from './firebase'

// Paths that may legitimately return 403 "Email not verified" and prefer to
// handle the response inline (e.g. the login page shows its own banner).
const EMAIL_UNVERIFIED_INLINE_PATHS = ['/v1/users/login']

let emailUnverifiedRedirectInFlight = false

const extractUnverifiedEmail = (body: any): string | null => {
  const candidates = [
    body?.error?.email,
    body?.error?.details?.email,
    body?.email
  ]
  for (const value of candidates) {
    if (typeof value === 'string' && value.length > 0) return value
  }
  return null
}

const isEmailNotVerifiedBody = (body: any): boolean => {
  const message =
    (typeof body?.error?.message === 'string' && body.error.message) ||
    (typeof body?.message === 'string' && body.message) ||
    ''
  return message.toLowerCase().includes('email not verified')
}

const redirectForEmailUnverified = async (email: string | null) => {
  if (emailUnverifiedRedirectInFlight) return
  emailUnverifiedRedirectInFlight = true

  document.cookie = `session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
  if (auth.currentUser) {
    try {
      await auth.signOut()
    } catch (err) {
      console.warn('Failed to sign out after email-unverified response', err)
    }
  }

  const target = email
    ? `/login?unverified=${encodeURIComponent(email)}`
    : '/login'
  window.location.replace(target)
}

export const apiFetch = async (
  path: string,
  options: RequestInit & { withCredentials?: boolean; mulipartHeaderTypeSet?: boolean } = {}
) => {
  const { withCredentials = false, mulipartHeaderTypeSet = false, ...restOptions } = options
  const API_URL = import.meta.env.VITE_BACKEND_URL
  const headers: HeadersInit = {}

  if (!mulipartHeaderTypeSet) {
    headers['Content-Type'] = 'application/json'
  }

  if (withCredentials) {
    const authToken = await getFirebaseAuthToken()
    headers.Authorization = `Bearer ${authToken}`
  }

  const response = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    headers,
    ...restOptions
  })

  if (
    response.status === 403 &&
    !EMAIL_UNVERIFIED_INLINE_PATHS.some((skipPath) => path.startsWith(skipPath))
  ) {
    try {
      const cloned = response.clone()
      const body = await cloned.json()
      if (isEmailNotVerifiedBody(body)) {
        const email = extractUnverifiedEmail(body) || auth.currentUser?.email || null
        await redirectForEmailUnverified(email)
      }
    } catch {
      // Body was not JSON or already consumed — ignore and let caller handle.
    }
  }

  return response
}
